import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { DialogLayer } from '../ui/Dialog';
import { countText, recipientsText } from './recipients';
import { REQUESTS_KEY, UNSENT_KEY, shortDateTime, type ConfirmationSent } from './types';
import { refreshQueries } from '../refresh';

type Named = { personId: string; name?: string };
/** Without the crew's field „Status” (review I3, rule 7a) only how many — never who is „Potwierdzony”. */
type Preview = { recipients?: Named[]; noContact?: Named[]; count?: number; noContactCount?: number; problem?: { code: string; message: string } };
type Result = { sent?: Named[]; noContact?: Named[]; sentCount?: number; noContactCount?: number };
const names = (list: { name?: string }[]) => (list.every((p) => p.name) ? list.map((p) => p.name).join(', ') : countText(list.length));
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : e ? fallback : null);

/**
 * „Wyślij potwierdzenie do obsady” (stage 2) — by hand, for the whole event (the crew tab's „…” and the people centre):
 * each person who goes gets the event's name, dates, place, their function and days; never the client or amounts.
 * The send is recorded. Owner's Q6: sent before — „Potwierdzenie było już wysłane [data]. Wysłać ponownie?” first.
 * Owner 2026-10-27 12:26: only people with „Potwierdzony”, and the dialog says to whom before „Wyślij” (the server's
 * list, read afresh each time it opens — „Wyślę do: Jan Kowalski”; rule 7a: how many); nobody confirmed yet — said.
 * Review M2: the send carries that list — changed meanwhile, the server refuses (409) and the dialog reads it again.
 * Review M4: why it cannot go (cancelled, over) comes with the list; a list that failed is said.
 */
export function ConfirmCrewDialog({ eventId, eventName, onClose }: { eventId: string; eventName: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const sends = useQuery({ queryKey: [...REQUESTS_KEY, 'event', eventId], queryFn: () => api<{ confirmations?: ConfirmationSent[] }>(`/api/requests/events/${eventId}`) });
  const last = (sends.data?.confirmations ?? []).at(-1);
  const preview = useQuery({ queryKey: [...REQUESTS_KEY, 'confirmation-to', eventId], staleTime: 0, refetchOnMount: 'always', queryFn: () => api<Preview>(`/api/requests/events/${eventId}/confirmation`) });
  const [changed, setChanged] = useState(false);
  const p = preview.data;
  const count = p ? (p.recipients ? p.recipients.length : (p.count ?? 0)) : 0;
  const noContact = p ? (p.noContact ? p.noContact.length : (p.noContactCount ?? 0)) : 0;
  const nobody = !!p && count === 0 && noContact === 0;
  const blocked = p?.problem && p.problem.code !== 'already_sent' ? p.problem.message : null;
  const confirm = useMutation({
    mutationFn: (again: boolean) =>
      api<Result>(`/api/requests/events/${eventId}/confirmation`, {
        method: 'POST',
        body: { ...(again ? { again: true } : {}), ...(p?.recipients ? { expected: p.recipients.map((r) => r.personId) } : { expectedCount: count }) },
      }),
    onMutate: () => setChanged(false),
    // review I2: the sent people leave „Do wysłania” over the grid
    onSuccess: () => Promise.all([queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }), refreshQueries(queryClient, UNSENT_KEY)]),
    onError: async (e) => {
      if (!(e instanceof ApiError && e.status === 409)) return;
      setChanged(true);
      await preview.refetch();
    },
  });
  const data = confirm.data;
  // sent meanwhile by someone else: the server asks the same question
  const alreadySent = confirm.error instanceof ApiError && confirm.error.code === 'already_sent' ? confirm.error.message : null;
  const conflict = confirm.error instanceof ApiError && confirm.error.status === 409;
  const error = alreadySent || conflict ? null : errorText(confirm.error, 'Nie udało się wysłać potwierdzenia');
  const again = !!last || !!alreadySent || p?.problem?.code === 'already_sent';
  const question = alreadySent ?? (p?.problem?.code === 'already_sent' ? p.problem.message : last ? `Potwierdzenie było już wysłane${last.sentAt ? ` ${shortDateTime(last.sentAt)}` : ''}. Wysłać ponownie?` : 'Wysłać potwierdzenie do obsady?');
  const sentCount = data ? (data.sent ? data.sent.length : (data.sentCount ?? 0)) : 0;
  const sentNoContact = data ? (data.noContact ? data.noContact.length : (data.noContactCount ?? 0)) : 0;
  const previewError = errorText(preview.error, 'Nie udało się sprawdzić, do kogo pójdzie potwierdzenie');
  return (
    <div className="dialog-backdrop">
      <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="crew-confirm-title" data-feature-id="requests.confirmation">
        <DialogLayer onClose={onClose} busy={confirm.isPending} />
        <h2 id="crew-confirm-title">{data ? 'Potwierdzenie wysłane' : question}</h2>
        {!data && (
          <p>
            „{eventName}”: każda osoba potwierdzona w obsadzie („Potwierdzony”) dostanie nazwę wydarzenia, termin, miejsce, swoją funkcję i swoje dni — bez klienta i kwot. Osoby „Proponowane” nie dostaną.
          </p>
        )}
        {!data && preview.isLoading && <p className="muted">Sprawdzam, do kogo…</p>}
        {!data && previewError && (
          <p className="alert" role="alert">
            {previewError}
          </p>
        )}
        {!data && blocked && <p className="alert">{blocked}</p>}
        {!data && !blocked && nobody && <p className="alert">Nikt z obsady nie jest jeszcze potwierdzony — nie ma do kogo wysłać potwierdzenia.</p>}
        {!data && changed && (
          <p className="alert" role="alert">
            Lista adresatów się zmieniła — sprawdź ją i wyślij ponownie.
          </p>
        )}
        {!data && p && !blocked && !nobody && <p className="recipients">{p.recipients ? recipientsText(p.recipients) : recipientsText(Array.from({ length: count }, () => ({})))}</p>}
        {!data && p && noContact > 0 && <p className="muted">Bez kontaktu (nie dostaną): {p.noContact ? names(p.noContact) : countText(noContact)}. Uzupełnij kontakt w Osoby.</p>}
        {data && sentCount > 0 && <p>{data.sent ? `Wysłano do: ${names(data.sent)}.` : `Wysłano do ${countText(sentCount, 'genitive')}.`}</p>}
        {data && sentNoContact > 0 && (
          <p className="alert" role="alert">
            Bez konta, e-maila i telefonu: {data.noContact ? names(data.noContact) : countText(sentNoContact)}. Uzupełnij kontakt w Osoby.
          </p>
        )}
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          {!data && !nobody && !blocked && !previewError && (
            <button className="btn primary" type="button" disabled={confirm.isPending || sends.isLoading || preview.isFetching || count === 0} onClick={() => confirm.mutate(again)}>
              {confirm.isPending ? 'Wysyłanie…' : again ? 'Wyślij ponownie' : 'Wyślij potwierdzenie'}
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            {data ? 'Zamknij' : 'Anuluj'}
          </button>
        </div>
      </div>
    </div>
  );
}
